import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from "react";

import { IFilterComp, IFilterParams } from "@ag-grid-community/core";
import Select from "antd/es/select";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import OptionValue from "@mapmycustomers/shared/types/customField/OptionValue";
import useRefState from "@mapmycustomers/shared/util/hook/useRefState";
import useRerenderer from "@mapmycustomers/shared/util/hook/useRerenderer";
import { Button } from "@mapmycustomers/ui";

import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import { OptionsCondition } from "@app/types/viewModel/agGridModel/AgGridFilterModel";

import styles from "./OptionsFilter.module.scss";

interface Props extends IFilterParams {
  customFieldId: CustomField["id"];
  multi: boolean;
  options: OptionValue[];
}

const OptionsFilter = forwardRef<Partial<IFilterComp>, Props>(
  ({ filterChangedCallback, multi, options }, ref) => {
    const intl = useIntl();

    const forceRender = useRerenderer();
    const [selectedOptionsValues, setSelectedOptionsValues] = useRefState<
      Set<OptionValue["value"]>
    >(new Set(), forceRender);

    useImperativeHandle(
      ref,
      () => {
        return {
          doesFilterPass(): boolean {
            // TODO: complete me (only needed for the client-side filtering)
            // const value: OptionCustomFieldValue[] = valueGetter(params.node);
            return false;
          },

          getModel(): OptionsCondition | undefined {
            return selectedOptionsValues.current.size > 0
              ? {
                  filterType: "optionsFilter",
                  operator: FilterOperator.IN_ANY,
                  value: Array.from(selectedOptionsValues.current),
                }
              : undefined;
          },
          isFilterActive(): boolean {
            return selectedOptionsValues.current.size > 0;
          },

          setModel(model: OptionsCondition | undefined) {
            if (model) {
              setSelectedOptionsValues(new Set(model.value));
            } else {
              setSelectedOptionsValues(new Set());
            }
          },
        };
      },
      [selectedOptionsValues, setSelectedOptionsValues]
    );

    const selectBoxOptions = useMemo(
      () =>
        options.map(({ displayName, value }) => ({
          label: displayName,
          value,
        })),
      [options]
    );

    const selectValues = useMemo(
      () => Array.from(selectedOptionsValues.current),
      // disable next line because we need to listen to changes inside the ref, not the ref itself
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedOptionsValues.current]
    );

    const handleOptionsSelectionChange = useCallback(
      (checkedValues: Array<OptionValue["value"]> | OptionValue["value"]) => {
        // @ts-ignore
        setSelectedOptionsValues(new Set(multi ? checkedValues : [checkedValues]));
      },
      [multi, setSelectedOptionsValues]
    );

    const handleApplyClick = useCallback(() => {
      filterChangedCallback();
    }, [filterChangedCallback]);

    const handleResetClick = useCallback(() => {
      setSelectedOptionsValues(new Set());
      forceRender();
      filterChangedCallback();
    }, [filterChangedCallback, forceRender, setSelectedOptionsValues]);

    return (
      <div className={styles.container}>
        <div>
          <Select<Array<OptionValue["value"]> | OptionValue["value"]>
            className={styles.select}
            // more info about this class: https://www.ag-grid.com/javascript-data-grid/component-filter/#custom-filters-containing-a-popup-element
            dropdownClassName="ag-custom-component-popup"
            filterOption={defaultFilterOption}
            mode={multi ? "multiple" : undefined}
            onChange={handleOptionsSelectionChange}
            options={selectBoxOptions}
            placeholder={intl.formatMessage({
              id: "grid.optionsFilter.select.placeholder",
              defaultMessage: "Click or type to select options",
              description: "Placeholder displayed in a OptionsFilter's select field",
            })}
            value={selectValues}
          />
        </div>
        <div className={styles.footer}>
          <Button onClick={handleResetClick} size="middle" type="default">
            {intl.formatMessage({
              id: "grid.optionsFilter.reset",
              defaultMessage: "Reset",
              description: "Reset button title in GridFilter tool panel",
            })}
          </Button>
          <Button onClick={handleApplyClick} size="middle" type="primary">
            {intl.formatMessage({
              id: "grid.optionsFilter.apply",
              defaultMessage: "Apply",
              description: "Apply button title in GridFilter tool panel",
            })}
          </Button>
        </div>
      </div>
    );
  }
);

export default OptionsFilter;
